import type { SongRequest } from '../services/requests';
import { arrayMove } from '@dnd-kit/sortable';

export interface ManualPlacement {
  manualOrder: number;
  otherOrders: { requestId: string; manualOrder: number }[];
}

export function clampQueuePosition(position: number, queueLength: number): number {
  if (!Number.isFinite(position)) return queueLength + 1;
  return Math.min(Math.max(Math.floor(position), 1), queueLength + 1);
}

export function placeInManualOrder(
  queue: Pick<SongRequest, 'id' | 'lockedNext'>[],
  position: number,
): ManualPlacement {
  const insertAt = clampQueuePosition(position, queue.length) - 1;
  const slots: (Pick<SongRequest, 'id' | 'lockedNext'> | null)[] = [...queue];
  slots.splice(insertAt, 0, null);

  let nextOrder = 1;
  let manualOrder = 1;
  const otherOrders: ManualPlacement['otherOrders'] = [];
  for (const slot of slots) {
    if (slot === null) {
      manualOrder = nextOrder++;
    } else if (!slot.lockedNext) {
      otherOrders.push({ requestId: slot.id, manualOrder: nextOrder++ });
    }
  }
  return { manualOrder, otherOrders };
}

export function hasManualPosition(request: Pick<SongRequest, 'manualOrder'>): boolean {
  return typeof request.manualOrder === 'number';
}

export function compareManualPositions(
  a: Pick<SongRequest, 'manualOrder'>,
  b: Pick<SongRequest, 'manualOrder'>,
): number {
  const aHas = hasManualPosition(a);
  const bHas = hasManualPosition(b);
  if (aHas && bHas) return (a.manualOrder as number) - (b.manualOrder as number);
  if (aHas) return -1;
  if (bHas) return 1;
  return 0;
}

export const PLAY_NEXT_ORDER_BASE = -1e13;

export function playNextManualOrder(nowMs: number = Date.now()): number {
  return PLAY_NEXT_ORDER_BASE + nowMs;
}

export function refreshKeepingListOrder<T extends { id: string }>(current: T[], fresh: T[]): T[] {
  const freshById = new Map(fresh.map(item => [item.id, item]));
  const kept: T[] = [];
  for (const existing of current) {
    const match = freshById.get(existing.id);
    if (match) kept.push(match);
  }
  const currentIds = new Set(current.map(item => item.id));
  const brandNew = fresh.filter(item => !currentIds.has(item.id));
  return [...kept, ...brandNew];
}

export type ManualOrderDropResult =
  | { updates: { requestId: string; manualOrder: number }[] }
  | { refused: 'locked' | 'missing' | 'noop' };

export function manualOrderAfterDrop(
  list: SongRequest[],
  activeId: string,
  overId: string,
  isDragLocked: (request: SongRequest) => boolean,
): ManualOrderDropResult {
  const from = list.findIndex(request => request.id === activeId);
  const to = list.findIndex(request => request.id === overId);
  if (from < 0 || to < 0) return { refused: 'missing' };
  if (isDragLocked(list[to])) return { refused: 'locked' };
  if (from === to) return { refused: 'noop' };
  const reordered = arrayMove(list, from, to);
  return {
    updates: reordered
      .filter(request => !request.lockedNext)
      .map((request, index) => ({ requestId: request.id, manualOrder: index + 1 })),
  };
}
